/**
 * Follower sprite sizing — pure, deterministic. No rendering/IO.
 *
 * Maps a species' Pokedex HEIGHT (metres) to a rendered-footprint scale, so a
 * Caterpie (0.3m) trails small while a Snorlax/Gyarados (huge) looms — but never
 * larger than the trainer it follows. The curve is a vaguely-sigmoid logistic
 * clamped to [MIN, MAX]:
 *
 *   scale = MIN + (MAX - MIN) / (1 + e^(-k*(h - h0)))   clamped to [MIN, MAX]
 *
 * MAX is a HARD CAP: a follower is at most 1.2x the trainer sprite, never more.
 * MIN is a floor so a tiny Pokemon stays visible. Tuned against real species
 * (caterpie 0.3, pikachu 0.4, bulbasaur 0.7, charizard 1.7, snorlax 2.1,
 * gyarados 6.5, onix 8.8, wailord 14.5) so small reads small, medium medium,
 * and the giants sit at the cap.
 */

/** Floor: a tiny follower (e.g. Caterpie) is still visible at 40% of the trainer. */
export const FOLLOWER_SCALE_MIN = 0.4;

/** Hard cap: a follower is never more than 120% of the trainer sprite. */
export const FOLLOWER_SCALE_MAX = 1.2;

/** Logistic midpoint (metres) — the height at the curve's inflection. */
export const FOLLOWER_SCALE_H0 = 1.3;

/** Logistic steepness — tuned for a pleasing spread across real species. */
export const FOLLOWER_SCALE_K = 2.0;

/**
 * Amplitude gain (>1): the bare logistic only *approaches* MAX asymptotically, so
 * the biggest real species (gyarados 6.5m, onix 8.8m, wailord 14.5m) would land at
 * ~1.1997 — visually the cap but never exactly it. A small overshoot makes the
 * clamp genuinely engage (giants hit 1.2 from ~2.7m up) while barely nudging the
 * mediums (charizard 1.7m stays ~0.985, clearly below the cap).
 */
export const FOLLOWER_SCALE_SPAN_GAIN = 1.06;

/** Fallback scale when a follower's species height is unknown. */
export const FOLLOWER_SCALE_DEFAULT = 0.6;

/**
 * Scale factor for a follower sprite given its species height in METRES.
 * Monotonic non-decreasing in `h`, always within [MIN, MAX]. A non-finite or
 * non-positive height yields the floor (treated as "tiny / unknown-ish").
 */
export function followerScale(heightMeters: number): number {
  if (!Number.isFinite(heightMeters) || heightMeters <= 0) {
    return FOLLOWER_SCALE_MIN;
  }
  const span = (FOLLOWER_SCALE_MAX - FOLLOWER_SCALE_MIN) * FOLLOWER_SCALE_SPAN_GAIN;
  const raw =
    FOLLOWER_SCALE_MIN +
    span / (1 + Math.exp(-FOLLOWER_SCALE_K * (heightMeters - FOLLOWER_SCALE_H0)));
  return clamp(raw, FOLLOWER_SCALE_MIN, FOLLOWER_SCALE_MAX);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
